import { StrictMode, Suspense, useEffect, useRef, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import {
  createBrowserRouter,
  createPath,
  Navigate,
  RouterProvider,
  useLocation,
  useParams,
  useRouteError,
  type RouteObject,
} from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './index.css';
import { installPressFeedback } from './lib/press';
import { Layout, navKind, useIsDesktop } from './components/Layout';
import { useStandalone } from './components/AuthDialog';
import { useMe } from './lib/session';
import { screens } from './lib/screens';
import {
  bootPrefetch,
  installIdlePrefetch,
  installIntentPrefetch,
  installNavigationPrefetch,
} from './lib/prefetch';
import { NotFound } from './pages/NotFound';
import { applyHead } from './lib/seo';
import { isAppMode } from './lib/appMode';
import { lazyComponent } from './lib/lazy';
import { hideLaunchSplash, launchSplashShowing } from './lib/splash';
import { SPLASH_MARK } from './lib/splash-mark';
import { AppUpdateGate } from './components/AppUpdateGate';
import { CountryGate } from './components/CountryGate';
import { startI18n, t, useLocale } from './i18n';

// The Android app's launch URL must never reach the router. src/launch.ts (its own small file,
// loaded ahead of this one) normally took the parameters out of the address bar already. If it
// hasn't run yet, hand it the query, clean the address bar here, and load it: it's the same file,
// so it runs once either way. (No static import: that would fold it into this bundle.)
if (/[?&]utm_source=twa(?:&|$)/i.test(window.location.search)) {
  (window as Window & { __gpLaunchSearch?: string }).__gpLaunchSearch = window.location.search;
  window.history.replaceState(
    window.history.state,
    '',
    window.location.pathname + window.location.hash,
  );
  void import('./launch');
}

const qc = new QueryClient({
  defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: true } },
});

// Pre-rendered public pages arrive with their markup in #root (vite.config.ts). Only visitors
// without a session cookie get those (worker/index.ts); everyone else gets the empty app shell.
const rootEl = document.getElementById('root')!;
const prerendered = rootEl.firstElementChild !== null;

// The session, the first screen's data and its code start now, together, before the first render.
bootPrefetch(qc, window.location.pathname, prerendered);

function Private({ children }: { children: ReactNode }) {
  const { data, isLoading } = useMe();
  const loc = useLocation();
  if (isLoading) return <div className="tile h-48 animate-pulse" aria-busy="true" />;
  if (!data)
    return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return <>{children}</>;
}

/** Same cream and mark as the Android splash screen and the launch splash in index.html (the mark
 *  160 px wide, centred), so the hand-off from one to the next is seamless. */
function Splash() {
  return (
    <div className="grid min-h-dvh place-items-center bg-canvas" aria-busy="true">
      <img
        src={SPLASH_MARK}
        alt="GaintPlay"
        width={160}
        height={160}
        className="gp-breathe h-40 w-40"
      />
    </div>
  );
}

/** The website's front page: its code is loaded only when someone signed out is going to see it. */
const Landing = lazyComponent(() => screens.landing().then((m) => m.Landing));

/** "/" served as the app shell: a session cookie is present (worker/index.ts), so this is nearly
 *  always a member on their way to Earn (the installed app's launch URL, a bookmark). Go there as
 *  soon as the session is known, without loading the website's front page; the installed app keeps
 *  its splash up meanwhile. A stale cookie gets the front page after all, or the app's sign-up
 *  screen. (A pre-rendered "/" is the front page itself: see the route table.) */
function Home() {
  const { data, isPending, isError } = useMe();
  const desktop = useIsDesktop();
  const installed = useStandalone() && !desktop;
  if (isPending)
    return installed ? <Splash /> : <div className="tile h-48 animate-pulse" aria-busy="true" />;
  if (data && !isError) return <Navigate to="/earn" replace />;
  if (installed) return <Navigate to="/signup" replace />;
  return (
    <Suspense fallback={<div className="tile h-48 animate-pulse" aria-busy="true" />}>
      <Landing />
    </Suspense>
  );
}

/** Launch URL of the installed app (manifest start_url /app?source=pwa): straight to Earn when
 *  signed in, to sign-up otherwise. While the session loads it shows the splash. */
function AppStart() {
  const { data, isPending, isError } = useMe();
  if (isPending) return <Splash />;
  return <Navigate to={data && !isError ? '/earn' : '/signup'} replace />;
}

/** In <main> while a screen's code arrives on a cold start; afterwards the router keeps the current
 *  screen on show until the next one's code is in. The same placeholder Private shows while the
 *  session loads, or the splash on the installed app's welcome routes (as AuthRoute shows). */
function ScreenFallback() {
  const { pathname } = useLocation();
  const desktop = useIsDesktop();
  const installed = useStandalone() && !desktop;
  if (installed && /^\/(signup|login|forgot-password)\/?$/.test(pathname)) return <Splash />;
  return <div className="tile h-48 animate-pulse" aria-busy="true" />;
}

const isChunkError = (e: unknown) =>
  e instanceof Error &&
  /dynamically imported module|module script failed|unable to preload/i.test(e.message);

/** A screen whose code couldn't be fetched (offline): said inside the shell, with a way to try
 *  again. Any other error goes on up to the router's own boundary, as before. */
function ScreenError() {
  const error = useRouteError();
  // The installed app's launch splash must not hide the message.
  useEffect(() => hideLaunchSplash(), []);
  if (!isChunkError(error)) throw error;
  return (
    <div
      role="alert"
      className="mx-auto flex min-h-[min(560px,62dvh)] max-w-[560px] flex-col items-center justify-center py-10 text-center sm:min-h-0 sm:py-20"
    >
      <h1 className="large-title lg:heading">
        {
          // i18n: a screen's code couldn't be downloaded (the phone is offline, say)
          t('This screen didn’t load.')
        }
      </h1>
      <p className="mx-auto mt-3 max-w-[40ch] text-pretty text-[16px] leading-relaxed text-graphite">
        {t('Check your connection and try again. Your coins are safe.')}
      </p>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="btn-primary mt-8 w-full max-w-[320px] py-3.5 text-[16px] sm:w-auto sm:py-3 sm:text-[15px]"
      >
        {t('Try again')}
      </button>
    </div>
  );
}

const RELOAD_KEY = 'gp_chunk_reload';
/** A tab left open across a deploy asks for code files that no longer exist: load the page afresh
 *  (at most once a minute, so a real outage can't loop) instead of showing an error. */
function staleChunk(err: unknown): Promise<never> {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (isChunkError(err) && Date.now() - last > 60_000) {
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
      window.location.assign(createPath(router.state.navigation.location ?? router.state.location));
      return new Promise<never>(() => {});
    }
  } catch {
    /* storage blocked: show the error instead */
  }
  return Promise.reject(err);
}

/** A route whose screen is its own chunk (lib/screens.ts). The router fetches the code before it
 *  shows the screen; lib/prefetch.ts usually has it (and the screen's data) on the way already. */
function page<M>(load: () => Promise<M>, render: (m: M) => ReactNode): Pick<RouteObject, 'lazy'> {
  return { lazy: () => load().then((m) => ({ element: render(m) }), staleChunk) };
}

/** Old site referral links (gaintplay.com/refer/CODE) keep working as this site's /r/CODE. */
function OldReferral() {
  const { code = '' } = useParams();
  return <Navigate to={`/r/${encodeURIComponent(code)}`} replace />;
}

/** Addresses of the old site (now old.gaintplay.com) that people bookmarked or shared. */
const OLD_SITE_PATHS: [string, string][] = [
  ['/offers', '/earn'],
  ['/reward', '/rewards'],
  ['/referrals', '/rewards'],
  ['/profile', '/account'],
  ['/faqs', '/faq'],
  ['/chat', '/support'],
];

const router = createBrowserRouter([
  { path: '/app', element: <AppStart /> },
  {
    element: <Layout />,
    children: [
      {
        hydrateFallbackElement: <ScreenFallback />,
        errorElement: <ScreenError />,
        children: [
          // The front page when pre-rendered (signed out); as the app shell, a hand-over to Earn.
          prerendered
            ? { path: '/', ...page(screens.landing, (m) => <m.Landing />) }
            : { path: '/', element: <Home /> },
          // Public guide pages (pre-rendered at build; src/pages/info/routes.tsx for the pre-render).
          { path: '/how-it-works', ...page(screens.howItWorks, (m) => <m.HowItWorks />) },
          { path: '/ways-to-earn', ...page(screens.waysToEarn, (m) => <m.WaysToEarn />) },
          { path: '/gift-cards', ...page(screens.giftCards, (m) => <m.GiftCards />) },
          { path: '/faq', ...page(screens.faq, (m) => <m.Faq />) },
          { path: '/signup', ...page(screens.auth, (m) => <m.AuthRoute mode="signup" />) },
          { path: '/login', ...page(screens.auth, (m) => <m.AuthRoute mode="login" />) },
          {
            path: '/forgot-password',
            ...page(screens.auth, (m) => <m.AuthRoute mode="forgot" />),
          },
          { path: '/reset-password', ...page(screens.auth, (m) => <m.ResetPassword />) },
          { path: '/verify-email', ...page(screens.auth, (m) => <m.VerifyEmail />) },
          { path: '/unsubscribe', ...page(screens.auth, (m) => <m.Unsubscribe />) },
          { path: '/r/:code', ...page(screens.auth, (m) => <m.ReferralLanding />) },
          {
            path: '/earn',
            ...page(screens.earn, (m) => (
              <Private>
                <m.Earn />
              </Private>
            )),
          },
          {
            path: '/support',
            ...page(screens.support, (m) => (
              <Private>
                <m.Support />
              </Private>
            )),
          },
          {
            path: '/support/:id',
            ...page(screens.support, (m) => (
              <Private>
                <m.SupportThread />
              </Private>
            )),
          },
          {
            path: '/wallet',
            ...page(screens.wallet, (m) => (
              <Private>
                <m.Wallet />
              </Private>
            )),
          },
          {
            path: '/cashout',
            ...page(screens.cashout, (m) => (
              <Private>
                <m.Cashout />
              </Private>
            )),
          },
          {
            path: '/rewards',
            ...page(screens.rewards, (m) => (
              <Private>
                <m.Rewards />
              </Private>
            )),
          },
          {
            path: '/account',
            ...page(screens.account, (m) => (
              <Private>
                <m.Account />
              </Private>
            )),
          },
          { path: '/leaderboard', ...page(screens.leaderboard, (m) => <m.Leaderboard />) },
          { path: '/terms', ...page(screens.legal, (m) => <m.Terms />) },
          { path: '/privacy', ...page(screens.legal, (m) => <m.Privacy />) },
          ...OLD_SITE_PATHS.map(([path, to]) => ({ path, element: <Navigate to={to} replace /> })),
          { path: '/refer/:code', element: <OldReferral /> },
          { path: '*', element: <NotFound /> },
        ],
      },
    ],
  },
]);
installNavigationPrefetch(qc, router);
installIntentPrefetch(qc);
installIdlePrefetch(qc, router);

/**
 * Screen transitions, for app mode only (signed in, below lg: <html data-app> plus the width, the
 * same test as index.css's `app:` variant). There, every navigation that changes the page runs
 * inside a View Transition (React Router's `viewTransition` option), so links and navigate() calls
 * don't each have to opt in; pass `viewTransition: false` (or `<Link viewTransition={false}>`) to
 * opt out. Same-page updates (search params, hashes) never animate. Before each change
 * <html data-nav> is set to tab | push | pop from the two routes (Layout's screenFor), which
 * index.css turns into a fade-through or a stack slide. The website (desktop, or signed out)
 * navigates instantly like any site: fading the page there only made the hard shadows blink.
 * So do browsers without the API, reduced motion, and the installed app while its launch splash
 * is still on show (the next screen takes its place under the splash; lib/splash.ts).
 */
function installViewTransitions(r: typeof router) {
  if (typeof document.startViewTransition !== 'function') return;
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const navigate = r.navigate.bind(r);
  r.navigate = ((to: Parameters<typeof navigate>[0], opts?: Parameters<typeof navigate>[1]) => {
    if (
      typeof to === 'number' ||
      to == null ||
      reduced.matches ||
      !isAppMode() ||
      launchSplashShowing()
    )
      return navigate(to as never, opts);
    if (opts && 'viewTransition' in opts && opts.viewTransition !== undefined)
      return navigate(to, opts);
    const path = typeof to === 'string' ? to : to.pathname;
    if (!path || path.startsWith('?') || path.startsWith('#')) return navigate(to, opts);
    const next = new URL(path, window.location.href).pathname;
    if (next === window.location.pathname) return navigate(to, opts);
    return navigate(to, { ...opts, viewTransition: true });
  }) as typeof r.navigate;
  let last = r.state.location.pathname;
  r.subscribe((state) => {
    const next = state.location.pathname;
    if (next === last) return;
    document.documentElement.dataset.nav = navKind(
      last,
      next,
      state.historyAction,
      state.location.state,
    );
    last = next;
  });
}
installViewTransitions(router);

/** <title>, description, canonical and structured data follow the route (lib/seo.ts), in the
 *  reader's language. The pre-rendered pages already ship the right head, so the first call
 *  changes nothing there. */
function installHead(r: typeof router) {
  let last = '';
  const sync = (state: typeof r.state, force = false) => {
    const notFound = state.matches.some((m) => m.route.path === '*');
    const key = `${state.location.pathname}|${notFound}`;
    if (key === last && !force) return;
    last = key;
    applyHead(state.location.pathname, { notFound });
  };
  sync(r.state);
  r.subscribe((state) => sync(state));
  return () => sync(r.state, true);
}

installPressFeedback();
// iOS Safari only applies :active (the press states) when a touch listener exists.
document.addEventListener('touchstart', () => {}, { passive: true });

// Offline page + cached assets for the installed app (public/sw.js). Production only, so local
// development never serves stale bundles.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/', updateViaCache: 'none' })
      .catch(() => {});
  });
}

let rehead = () => {};

/** The app, drawn again from the top in a newly picked language (and its <head> with it). */
function App() {
  const locale = useLocale();
  const first = useRef(locale);
  useEffect(() => {
    if (locale !== first.current) rehead();
  }, [locale]);
  return <RouterProvider key={locale} router={router} />;
}

function mount() {
  createRoot(rootEl).render(
    <StrictMode>
      <QueryClientProvider client={qc}>
        <AppUpdateGate>
          <CountryGate>
            <App />
          </CountryGate>
        </AppUpdateGate>
      </QueryClientProvider>
    </StrictMode>,
  );
}

// The language and its words come first, so nothing is drawn in English and then swapped (a
// pre-rendered page in another language already shows it). Then createRoot replaces #root's markup
// with its first render. On a pre-rendered page that render waits for the page's code (the router
// has it once `initialized`), so it is the same markup and the swap is invisible: no fallback, no
// blank frame. Until then the pre-rendered page is on screen; if its code can't be fetched at all,
// it stays as a static page (its links still work).
void startI18n().then(() => {
  rehead = installHead(router);
  if (!prerendered || router.state.initialized) mount();
  else {
    const stop = router.subscribe((state) => {
      if (!state.initialized) return;
      stop();
      if (!state.errors) mount();
    });
  }
});
